import { Button } from "@/components/ui/button";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useState, useEffect } from "react";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";
import { supabase } from "@/integrations/supabase/client";

interface PlanSuggestion {
  code: string;
  name: string;
  price: number;
}

export function SubscriptionProgress() {
  const { loading, currentPlan, patientsCount, getMaxPatients, isFree } = useSubscriptionStore();
  const [showModal, setShowModal] = useState(false);
  const [nextPlan, setNextPlan] = useState<PlanSuggestion | null>(null);

  const maxPatients = getMaxPatients();
  const progressPercentage = maxPatients 
    ? Math.round((patientsCount / maxPatients) * 100)
    : 0;
  const patientsRemaining = maxPatients 
    ? Math.max(0, maxPatients - patientsCount)
    : null;

  useEffect(() => {
    const loadNextPlan = async () => {
      if (!currentPlan) return;

      const { data: plans } = await supabase
        .from('subscription_plans')
        .select('code, name, price_cents')
        .order('display_order', { ascending: true });

      if (!plans || plans.length === 0) return;

      // Lógica de sugestão de plano
      let suggestedPlan: PlanSuggestion | null = null;

      if (currentPlan.code === 'free') {
        // Free → Starter
        const plan = plans.find(p => p.code === 'starter');
        if (plan) suggestedPlan = { code: plan.code, name: plan.name, price: plan.price_cents };
      } else if (currentPlan.code === 'starter') {
        // Starter → Plus
        const plan = plans.find(p => p.code === 'plus');
        if (plan) suggestedPlan = { code: plan.code, name: plan.name, price: plan.price_cents };
      } else if (currentPlan.code === 'plus') {
        // Plus → Premium
        const plan = plans.find(p => p.code === 'premium');
        if (plan) suggestedPlan = { code: plan.code, name: plan.name, price: plan.price_cents };
      } else if (currentPlan.code === 'premium') {
        // Premium → Enterprise (se existir)
        const plan = plans.find(p => p.code === 'enterprise');
        if (plan) suggestedPlan = { code: plan.code, name: plan.name, price: plan.price_cents };
      }

      // Fallback: se não encontrou plano específico, pega o próximo na ordem
      if (!suggestedPlan) {
        const currentIndex = plans.findIndex(p => p.code === currentPlan.code);
        if (currentIndex >= 0 && currentIndex < plans.length - 1) {
          const nextPlanData = plans[currentIndex + 1];
          suggestedPlan = {
            code: nextPlanData.code,
            name: nextPlanData.name,
            price: nextPlanData.price_cents
          };
        }
      }

      if (suggestedPlan) {
        setNextPlan(suggestedPlan);
      }
    };

    loadNextPlan();
  }, [currentPlan]);

  if (loading || !currentPlan) {
    return (
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg px-4 py-2.5">
        <div className="h-10 w-full animate-pulse bg-muted rounded" />
      </div>
    );
  }

  // Se não tem limite (ilimitado), não mostrar o card
  if (maxPatients === null) {
    return null;
  }

  return (
    <>
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg px-4 py-2.5">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs font-medium text-foreground/70">
                {currentPlan.name}
              </span>
              <span className="text-xs font-semibold text-foreground">
                {patientsCount}/{maxPatients}
              </span>
            </div>
            
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div 
                className="h-full transition-all bg-gradient-to-r from-primary to-secondary"
                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
              />
            </div>
            
            {patientsRemaining !== null && patientsRemaining > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {patientsRemaining} {patientsRemaining === 1 ? 'paciente restante' : 'pacientes restantes'}
              </p>
            )}
            {patientsRemaining === 0 && (
              <p className="text-[10px] text-amber-600 mt-1 font-medium">
                Limite atingido
              </p>
            )}
          </div>
          
          {nextPlan && (
            <Button
              size="sm"
              onClick={() => setShowModal(true)}
              className="flex-shrink-0 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground h-8 px-4 text-xs font-medium transition-opacity"
            >
              Assinar {nextPlan.name.split(' ')[0]}
            </Button>
          )}
        </div>
      </div>

      {nextPlan && (
        <SubscriptionModal
          open={showModal}
          onOpenChange={setShowModal}
          planCode={nextPlan.code}
          planName={nextPlan.name}
          planPrice={nextPlan.price}
          onSuccess={() => {
            setShowModal(false);
            useSubscriptionStore.getState().loadSubscription();
            window.location.reload();
          }}
        />
      )}
    </>
  );
}
